import { cartApiService } from "@/src/lib/api-services/cartApiServices";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    updateSelectedDateTime,
    updateCurrentStep,
    appointmentSteps,
    updateCentreDetails,
} from "@/src/lib/store/pet-ct-appointment-new/slice";
import Iconify from "@/src/components/iconify";
import IconCode from "@/src/lib/iconCode";
import { useDispatch } from "react-redux";
import { formatTimeWithAmPm } from "@/src/components/common/common";
import DateSkeleton from "./DateSkeleton";
import { useHODMutation } from "@/src/lib/hooks/useHODMutation";
import { useToast } from "@/src/lib/hooks/useToast";
import Progressbar from "@/src/components/loading";

const DateTime = ({ onClose, confirmTimeSlot = false }) => {
    const { showCustomToast } = useToast();
    const [dates, setDates] = useState(null);
    const [isAvailableSlots, setIsAvailableSlots] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [selected, setSelected] = useState({ date: null, slotTime: null });
    const dispatch = useDispatch();
    const testDetails = useSelector(
        (state) => state?.appointmentNew?.testDetails
    );
    const centreDetails = useSelector(
        (state) => state?.appointmentNew?.centreDetails
    );
    const checkoutId = useSelector(
        (state) => state?.appointmentNew?.checkoutId
    );

    useEffect(() => {
        const fetchDates = async () => {
            if (!testDetails?.itemId || !centreDetails?.centreId) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const response = await cartApiService.getPetCtDate({
                    testId: String(testDetails.itemId),
                    centreId: centreDetails.centreId,
                    validationType: "getDates",
                    offsetBy: isAvailableSlots,
                    hideEarlySlot: 1,
                });
                const monthData = response?.data ?? null;
                setDates(monthData);
            } catch (error) {
                console.error(
                    "Failed to fetch PET-CT dates:",
                    error?.response?.data?.message ?? error?.message
                );
            } finally {
                setIsLoading(false);
            }
        };
        fetchDates();
    }, [testDetails?.itemId, centreDetails?.centreId, isAvailableSlots]);

    const availableDates = useMemo(() => dates?.availableDates ?? [], [dates]);

    const getDayName = useCallback((yyyyMmDd) => {
        const [y, m, d] = yyyyMmDd.split("-").map((v) => Number(v));
        const dt = new Date(y, (m || 1) - 1, d || 1);
        return dt.toLocaleDateString("en-US", { weekday: "long" });
    }, []);

    const getRelativeLabel = useCallback((yyyyMmDd) => {
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);

        const fmt = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
                2,
                "0"
            )}-${String(d.getDate()).padStart(2, "0")}`;

        if (yyyyMmDd === fmt(today)) return "Today";
        if (yyyyMmDd === fmt(tomorrow)) return "Tomorrow";
        return null;
    }, []);

    const formatHumanDate = useCallback((yyyyMmDd) => {
        const [y, m, d] = yyyyMmDd.split("-").map((v) => Number(v));
        const dt = new Date(y, (m || 1) - 1, d || 1);
        return dt.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short", //  year: "numeric",
        }); // e.g., 26 Nov 2025
    }, []);

    const handleSelectTime = useCallback(
        (dateStr, slotTime) => {
            if (!dateStr || !slotTime) return;

            if (confirmTimeSlot) {
                const payload = {
                    checkoutId: checkoutId,
                    preferredDate: dateStr,
                    preferredTime: slotTime.split(".")[0],
                };

                updatePetCtAppointmentMutate(payload);
                return;
            }
            setSelected({ date: dateStr, slotTime });
            dispatch(updateSelectedDateTime({ date: dateStr, slotTime }));
            dispatch(updateCurrentStep(appointmentSteps.SELECT_PATIENT));
        },
        [confirmTimeSlot, onClose]
    );

    const {
        mutate: updatePetCtAppointmentMutate,
        isLoading: isUpdatingPetCtAppointment,
    } = useHODMutation((data) => cartApiService.updatePetCtAppointment(data), {
        onSuccess: (data, variables) => {
            dispatch(
                updateSelectedDateTime({
                    date: variables.preferredDate,
                    slotTime: variables.preferredTime,
                })
            );
            showCustomToast("Appointment updated successfully", "success");
            onClose?.();
        },
        onError: (error) => {
            showCustomToast(
                error?.response?.data?.message || error?.message,
                "error"
            );
        },
    });

    const handlePreviousSlots = useCallback(() => {
        setIsAvailableSlots((prev) => prev - 1);
    }, [isAvailableSlots]);

    const handleNextSlots = useCallback(() => {
        setIsAvailableSlots((prev) => prev + 1);
    }, [isAvailableSlots]);

    const handleCheckNearbyCentres = useCallback(() => {
        dispatch(updateCurrentStep(appointmentSteps.SELECT_CENTRE));
        dispatch(updateCentreDetails({}));
    }, []);

    return (
        <>
            <p
                className={`uppercase tracking-widest text-black  md:mb-2 mb-1 ${
                    confirmTimeSlot
                        ? "md:mt-0 mt-4 text-base font-bold"
                        : "md:mt-10 mt-4 text-xs font-medium"
                }`}
            >
                Select Date & Time
            </p>
            <div className="md:px-6 md:py-10 py-4 px-3 bg-white shadow-md">
                {isLoading ? (
                    <DateSkeleton />
                ) : (
                    <>
                        <p className="text-xs uppercase tracking-widest text-black font-medium md:mb-3 md:mb-8 mb-4 text-center">
                            Time Zone: India Standard Time (GMT+05:30)
                        </p>

                        <div className="flex flex-wrap justify-center gap-4">
                            {availableDates.length > 0 ? (
                                availableDates.map((day, idx) => {
                                    const relative = getRelativeLabel(
                                        day.exactDate
                                    );
                                    const weekday = getDayName(day.exactDate);
                                    const human = formatHumanDate(
                                        day.exactDate
                                    );
                                    const slots = day.availableSlots || [];
                                    const isSelectedDate =
                                        selected.date === day.exactDate;

                                    return (
                                        <div
                                            key={day.exactDate}
                                            className="w-[145px]"
                                        >
                                            <div className="relative md:mb-6 mb-3 text-center flex items-center justify-center">
                                                {idx === 0 &&
                                                    isAvailableSlots > 0 && (
                                                        <button
                                                            onClick={
                                                                handlePreviousSlots
                                                            }
                                                            type="button"
                                                            aria-label="Prev slots"
                                                            className="absolute left-0 p-1 rounded hover:bg-primary/20 cursor-pointer"
                                                        >
                                                            <Iconify
                                                                icon={
                                                                    IconCode.arrowRight
                                                                }
                                                                width={24}
                                                                height={24}
                                                                className="text-gray-700 rotate-180"
                                                            />
                                                        </button>
                                                    )}
                                                <div className="flex flex-col items-center justify-center">
                                                    {relative ? (
                                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-3">
                                                            {relative}
                                                        </div>
                                                    ) : (
                                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-3 invisible">
                                                            {weekday?.toLowerCase()}
                                                        </div>
                                                    )}
                                                    <div className="text-sm text-black-800 capitalize font-semibold mb-0.5 tracking-wide">
                                                        {weekday?.toLowerCase()}
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {human}
                                                    </div>
                                                </div>
                                                {idx ===
                                                    availableDates.length - 1 &&
                                                    isAvailableSlots < 5 && (
                                                        <button
                                                            onClick={
                                                                handleNextSlots
                                                            }
                                                            type="button"
                                                            aria-label="Next slots"
                                                            className="absolute right-0 p-1 rounded hover:bg-primary/20 cursor-pointer"
                                                        >
                                                            <Iconify
                                                                icon={
                                                                    IconCode.arrowRight
                                                                }
                                                                width={24}
                                                                height={24}
                                                                className="text-gray-700"
                                                            />
                                                        </button>
                                                    )}
                                            </div>

                                            <div className="flex flex-col gap-3 max-h-96 overflow-auto">
                                                {slots.length === 0 ? (
                                                    <div className="text-xs text-gray-500">
                                                        No slots available
                                                    </div>
                                                ) : (
                                                    slots.map((slot, idx) => {
                                                        const label =
                                                            formatTimeWithAmPm(
                                                                slot.slotTime
                                                            );
                                                        const isSelected =
                                                            isSelectedDate &&
                                                            selected.slotTime ===
                                                                slot.slotTime;
                                                        return (
                                                            <button
                                                                key={`${day.exactDate}-${slot.slotTime}`}
                                                                type="button"
                                                                onClick={() =>
                                                                    handleSelectTime(
                                                                        day.exactDate,
                                                                        slot.slotTime
                                                                    )
                                                                }
                                                                className={`w-full font-semibold px-3 py-5 border transition leading-tight cursor-pointer ${
                                                                    isSelected
                                                                        ? "bg-primary text-white border-primary"
                                                                        : "bg-white text-gray-700 hover:bg-primary/10 border-slate-300 hover:border-primary/30"
                                                                }`}
                                                            >
                                                                {label}
                                                            </button>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-xs text-primary text-center bg-primary/10 p-2 rounded-md w-full">
                                    No dates available
                                </div>
                            )}
                        </div>

                        {!confirmTimeSlot && (
                            <p className="text-xs text-gray-500 text-center mt-5">
                                No slots available ?{" "}
                                <span
                                    className="text-primary cursor-pointer underline"
                                    onClick={handleCheckNearbyCentres}
                                >
                                    check nearby centres
                                </span>
                            </p>
                        )}
                    </>
                )}
            </div>

            {isUpdatingPetCtAppointment && (
                <div className="flex justify-center items-center h-full w-full mt-4 text-primary">
                    <Progressbar size={24} />{" "}
                    <span className="ml-2">please wait...</span>
                </div>
            )}
        </>
    );
};

export default DateTime;
